/* ═══════════════════════════════════════════════════════════════
   Org Chart V2 — right-click edge menu. Rebuilt on the shared kit
   ColorPickerPopover (People Rebuild §2/§3 — "color → shared
   ColorPicker"); default line color is the dark `var(--tx)` token
   sentinel (Josh 2026-08-17: --tm read too light on a 1.5px line),
   stored as `undefined` in the DB. Portal'd, viewport-clamped,
   dismisses on mousedown-outside (data-orgchart-menu and
   data-colorpicker-panel exempt) + Esc.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import {
  ArrowUp,
  ArrowRight,
  ArrowDown,
  ArrowLeft,
  Tag,
  RotateCcw,
  CircleDot,
  CornerDownRight,
  X,
  Trash2,
} from 'lucide-react';
import type { OrgChartEdge } from '../../../../../shared/entities';
import { ColorPickerPopover } from '../../../../components/ui/color-picker';
import { ORG_EDGE_COLORS } from '../../../../lib/palette';
import { Icon } from '../../../../components/ui/Icon';
import { rem } from '../../../../lib/rem';

// Same hue set as the shared ORG_EDGE_COLORS constant, minus its old
// `var(--bd)` "default" sentinel entry — the sentinel is now its own
// dedicated "Default" swatch (var(--tx)) rendered alongside the
// picker, not a swatch value inside it (a CSS var isn't a real color
// a hex-based picker can represent).
const CUSTOM_SWATCHES = ORG_EDGE_COLORS.slice(1);

const menuActionButtonStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  width: '100%',
  minHeight: 'var(--space-6)',
  padding: `${rem(6)} ${rem(10)}`,
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--bd)',
  background: 'var(--sf)',
  color: 'var(--tx)',
  cursor: 'pointer',
  fontSize: 'calc(var(--font-size-base) * 12 / 13)',
  textAlign: 'left',
};

const sectionLabelStyle: React.CSSProperties = {
  color: 'var(--tm)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '.04em',
  marginBottom: rem(6),
  display: 'block',
};

export interface EdgeMenuProps {
  edge: OrgChartEdge;
  x: number;
  y: number;
  onUpdateEdge: (patch: Partial<OrgChartEdge>) => void;
  onDeleteEdge: () => void;
  onEditLabel: () => void;
  /** Spawns a junction on this line at the point the menu was opened
   *  from (People Rebuild, Josh 2026-08-17: the old drag-off-the-line
   *  branch handle was incomprehensible — junctions are now
   *  menu-driven only). */
  onAddJunctionHere: () => void;
  /** Inserts a turn (waypoint) at the point the menu was opened from,
   *  projected onto the line's current rendered path — converts the
   *  edge to manual if it was auto, exactly like segment-drag does
   *  (People Rebuild, Josh law 2026-08-18: "lines are straight
   *  segments between turns... I add/delete turns at will"). */
  onAddTurnHere: () => void;
  /** True when the right-click that opened this menu landed within
   *  12px of an existing turn — shows "Delete turn" for it. */
  nearTurn?: boolean;
  onDeleteTurnHere?: () => void;
}

export function EdgeMenu({
  edge,
  x,
  y,
  onUpdateEdge,
  onDeleteEdge,
  onEditLabel,
  onAddJunctionHere,
  onAddTurnHere,
  nearTurn,
  onDeleteTurnHere,
}: EdgeMenuProps) {
  const sides: {
    value: 'top' | 'right' | 'bottom' | 'left';
    SideIcon: typeof ArrowUp;
    label: string;
  }[] = [
    { value: 'top', SideIcon: ArrowUp, label: 'Top' },
    { value: 'right', SideIcon: ArrowRight, label: 'Right' },
    { value: 'bottom', SideIcon: ArrowDown, label: 'Bottom' },
    { value: 'left', SideIcon: ArrowLeft, label: 'Left' },
  ];
  const styles: { value: 'solid' | 'dashed' | 'dotted'; label: string }[] = [
    { value: 'solid', label: 'Solid' },
    { value: 'dashed', label: 'Dashed' },
    { value: 'dotted', label: 'Dotted' },
  ];
  const MENU_W = 236;
  const menuX = Math.min(Math.max(8, x), window.innerWidth - MENU_W - 8);
  const menuY = Math.min(Math.max(8, y), window.innerHeight - 420);
  const isDefault = !edge.color;

  return (
    <div
      data-orgchart-menu
      role="menu"
      aria-label="Line options"
      style={{
        position: 'fixed',
        left: menuX,
        top: menuY,
        zIndex: 1000,
        background: 'var(--sa)',
        border: '1px solid var(--bd)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
        padding: rem(10),
        width: MENU_W,
        fontSize: 'var(--font-size-base)',
      }}
    >
      <div style={{ marginBottom: rem(10) }}>
        <span style={sectionLabelStyle}>Connects from / to</span>
        <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
          {sides.map(({ value: s, SideIcon, label }) => {
            const active = (edge.sourceHandle || '') === s;
            return (
              <button
                key={`from-${s}`}
                title={`Exit from the ${label.toLowerCase()} side`}
                aria-label={`Exit from the ${label.toLowerCase()} side`}
                aria-pressed={active}
                onClick={() => onUpdateEdge({ sourceHandle: s })}
                style={{
                  width: 'var(--space-6)',
                  height: 'var(--space-6)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--bd)',
                  background: active ? 'var(--ac)' : 'var(--sf)',
                  color: active ? 'var(--color-text-inverse)' : 'var(--tx)',
                  cursor: 'pointer',
                }}
              >
                <Icon icon={SideIcon} em={1.0769} aria-hidden="true" />
              </button>
            );
          })}
          <span
            aria-hidden="true"
            style={{
              alignSelf: 'center',
              color: 'var(--tm)',
              margin: `0 ${rem(2)}`,
              display: 'inline-flex',
            }}
          >
            <Icon icon={ArrowRight} em={0.9231} />
          </span>
          {sides.map(({ value: s, SideIcon, label }) => {
            const active = (edge.targetHandle || '') === s;
            return (
              <button
                key={`to-${s}`}
                title={`Enter from the ${label.toLowerCase()} side`}
                aria-label={`Enter from the ${label.toLowerCase()} side`}
                aria-pressed={active}
                onClick={() => onUpdateEdge({ targetHandle: s })}
                style={{
                  width: 'var(--space-6)',
                  height: 'var(--space-6)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--bd)',
                  background: active ? 'var(--ac)' : 'var(--sf)',
                  color: active ? 'var(--color-text-inverse)' : 'var(--tx)',
                  cursor: 'pointer',
                }}
              >
                <Icon icon={SideIcon} em={1.0769} aria-hidden="true" />
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ marginBottom: rem(10) }}>
        <span style={sectionLabelStyle}>Line style</span>
        <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
          {styles.map((s) => {
            const active = (edge.style || 'solid') === s.value;
            return (
              <button
                key={s.value}
                onClick={() => onUpdateEdge({ style: s.value })}
                style={{
                  flex: 1,
                  minHeight: 'var(--space-6)',
                  padding: 'var(--space-1) var(--space-2)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--bd)',
                  background: active ? 'var(--ac)' : 'var(--sf)',
                  color: active ? 'var(--color-text-inverse)' : 'var(--tx)',
                  cursor: 'pointer',
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ marginBottom: rem(10) }}>
        <span style={sectionLabelStyle}>Color</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: rem(6) }}>
          <button
            type="button"
            onClick={() => onUpdateEdge({ color: undefined })}
            title="Use the theme default line color"
            aria-label="Use default line color"
            aria-pressed={isDefault}
            style={{
              width: 'var(--space-6)',
              height: 'var(--space-6)',
              borderRadius: 'var(--radius-md)',
              border: isDefault ? '2px solid var(--tx)' : '1px solid var(--bd)',
              background: 'var(--sf)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <span
              style={{
                width: 'var(--space-4)',
                height: 'var(--space-4)',
                borderRadius: '50%',
                background: 'var(--tx)',
                display: 'block',
              }}
            />
          </button>
          <ColorPickerPopover
            value={edge.color || '#8a8f98'}
            onChange={(hex) => onUpdateEdge({ color: hex })}
            allowAlpha={false}
            swatches={CUSTOM_SWATCHES}
            aria-label="Custom line color"
          />
          <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)' }}>
            {isDefault ? 'Default' : 'Custom'}
          </span>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-1)',
          borderTop: '1px solid var(--bd)',
          paddingTop: 'var(--space-2)',
        }}
      >
        <button onClick={onEditLabel} style={menuActionButtonStyle}>
          <Icon icon={Tag} em={1.1667} aria-hidden="true" />
          {edge.label ? 'Edit label' : 'Add label'}
        </button>
        {(edge.waypoints?.length ?? 0) > 0 && (
          <button
            onClick={() => onUpdateEdge({ waypoints: [] })}
            title="Clear all turns and junctions on this line and let it auto-route"
            style={menuActionButtonStyle}
          >
            <Icon icon={RotateCcw} em={1.1667} aria-hidden="true" />
            Reset shape
          </button>
        )}
        <button
          onClick={onAddTurnHere}
          title="Add a turn at the point you right-clicked, so you can shape this line"
          aria-label="Add turn here"
          style={menuActionButtonStyle}
        >
          <Icon icon={CornerDownRight} em={1.1667} aria-hidden="true" />
          Add turn here
        </button>
        {nearTurn && onDeleteTurnHere && (
          <button
            onClick={onDeleteTurnHere}
            title="Remove the turn you right-clicked — its two segments merge into one"
            aria-label="Delete turn"
            style={menuActionButtonStyle}
          >
            <Icon icon={X} em={1.1667} aria-hidden="true" />
            Delete turn
          </button>
        )}
        <button
          onClick={onAddJunctionHere}
          title="Drop a junction on this line at the point you right-clicked"
          aria-label="Add junction here"
          style={menuActionButtonStyle}
        >
          <Icon icon={CircleDot} em={1.1667} aria-hidden="true" />
          Add junction here
        </button>
        <button
          onClick={onDeleteEdge}
          style={{
            ...menuActionButtonStyle,
            background: 'color-mix(in srgb, var(--dg) 12%, transparent)',
            color: 'var(--dg)',
          }}
        >
          <Icon icon={Trash2} em={1.1667} aria-hidden="true" />
          Delete line
        </button>
      </div>
    </div>
  );
}
